/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 3 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import { getFilesList } from "@Services/dataManager";
import { convertDate } from "@Services/luxonHelper";
import { getReviewUrl } from "@Services/utils/helpers";
import { getViewerUrl, MAX_FILE_UPLOAD_SIZE, buildAllowedExtensionsAccept, formCategories } from "@Helpers";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import FileVersion from ".";
import { QueryBuilder, executeQuery } from "@Services/queryBuilder";
import { getStatusBtnStyle } from "@Features/reviews";
import { getFilesGridColumns } from "./FilesGridHelper";
import type { FilesGridColumn, ReviewLevelOrSentinel } from "./FilesGridHelper";
import type {
  GroupCondition,
  LayoutData,
  SlickGrid1Ref,
  ToolbarItem,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import axiosWrapper from "@Services/axiosWrapper";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import type { GetFormTemplateResponse, GetAllUsedFormTemplatesResponse } from "@ServerTypes/projects/project.schema";
import _ from "lodash";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import DropdownButtonComponent from "@CubeComponents/form/DropdownButtonComponentV1";
// Identity assertion: the alias is emitted, so it stays; only the `any` is dropped.
const TypedDropdownButtonComponent = DropdownButtonComponent as typeof DropdownButtonComponent;

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import {
  setCurrentFolderInFileManager,
  setIsFileVersionUploading,
  setCurrentFileUploadProperties,
  minimiseFileUpload,
  maximiseFileUpload,
  closeFileUpload,
  emptyFileUploadData,
} from "@Actions/fileUpload";

import { useSearchParams, useLocation } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import SplitLayout from "@CubeComponents/html/SplitLayout/SplitLayout";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { GridPageLayout, GridPageHeaderLeft } from "@CubeComponents/html/GridPageLayout";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { FileUploadFormTemplate } from "@Reducers/minimiseFileUpload";

interface ShowToolbarOptions {
  views?: boolean;
  group?: boolean;
  upload?: boolean;
  action?: boolean;
  pinnedColumns?: boolean;
}

interface FilesGridProps {
  initialShowAllVersions?: boolean;
  showProjectID?: boolean;
  projectId?: string | null;
  showTitle?: boolean;
  showToolbar?: ShowToolbarOptions;
  propertiesPanel?: boolean;
  excelExport?: boolean;
  handleFileSelectionCallback?: (selectedFiles: SelectedFilePayload[]) => void;
  spaceId?: string | null;
  showOnlyMergedFile?: boolean;
  disableMenuOptions?: boolean;
  showUpload?: boolean;
  disableFiles?: string[];
}

/**
 * A row of this grid. Declared as a `type` alias, NOT an `interface`, on purpose: only a type alias
 * gets an implicit index signature, and `FilesGridRow` (the helper's row constraint) declares one —
 * an `interface` here fails `TRow extends FilesGridRow` with no obvious cause.
 */
type FileData = {
  /** Custom-field columns address values by dotted path (`customFields.<questionId>`), so `field`
   *  — which is `keyof FileData` — has to admit an arbitrary string. */
  [key: string]: unknown;
  /** SlickGrid's row key — set explicitly for every row `buildFilesWithReviews` emits. */
  id: string | number;
  fileVersionId?: string;
  /**
   * Everything below comes out of the unvalidated `getFilesList` answer (`RawFileRecord`), which
   * cannot promise any of it — the same reason `FilesGridRow`, the helper's row constraint,
   * declares the identical members optional. Declaring them required here only moved the problem
   * to an `as FileData` on the two rows this file builds.
   */
  fileName?: string;
  fileType?: string;
  size?: number;
  createdDate?: string;
  createdBy?: {
    _id?: ObjectIdString;
  };
  file?: {
    space?: {
      id: string;
      project?: {
        id: string;
        projectId: string;
      };
    };
    indexAtProjectLevel: number;
  };
  fileVersionNumber?: number;
  isContainer?: boolean;
  resourceType?: string;
  reviews?: FileReviewSummary;
  customFields?: Record<string, unknown>;
  parentFileVersionId?: string | null;
  currentReviewLevel?: ReviewLevelOrSentinel;
  reviewName?: string;
  serialId?: string | number;
  reviewFlow?: string;
  reviewStatusName?: string;
  nextActionDueDate?: string | number;
  isRowDisabled?: boolean;
};

/**
 * `GetFormTemplateResponse` types `template` as `unknown[]`, so the element shape is declared here
 * from what `processTemplate` reads off it.
 */
type FormTemplateSection = { questionFields?: FormTemplateQuestion[]; sectionHeading?: string };

/**
 * The saved view this grid applies. `LayoutData` is SlickGrid1's shape; the two extra keys are what
 * `onClickLayout` actually destructures.
 *
 * KNOWN BUG, preserved verbatim: `LayoutData` declares the grouping list as `groupConditions`, but
 * `onClickLayout` reads `group` — so unless a view document carries a legacy `group` key, the
 * grouping branch is dead and the `else` branch always runs. Verify against a saved view before
 * changing it; renaming the read would alter behaviour.
 */
type FilesGridSavedView = Omit<LayoutData, "pinnedColumns"> & {
  group?: GroupCondition[];
  enableAllVersions?: boolean;
  /**
   * Same as `LayoutData.pinnedColumns`: the IDS of the pinned columns. This used to be declared
   * `number` here "as what this file does with it"; `createNewLayoutData()` in SlickGrid1 persists
   * the `string[]` of column ids, so `LayoutData` is the side that was right — see the note on the
   * `else` branch of `onClickLayout`.
   */
  pinnedColumns?: string[];
};

/** One question inside a form-template section; each becomes a custom-field grid column. */
type FormTemplateQuestion = { _id: string; fieldType?: string; questionValue?: string };

/**
 * A file record exactly as `getFilesList` returns it. Deliberately loose: the query is built through
 * `QueryBuilder`/`executeQuery`, which have no response schema, and this component both reads
 * arbitrary keys off it and spreads it wholesale into a row. Typing it `unknown`-valued keeps every
 * read explicit without pretending to a shape the server does not guarantee.
 */
type RawFileRecord = { [key: string]: unknown; id?: unknown; fileName?: string; resourceType?: string };

/**
 * The files-list answer. `executeQuery` declares only its `{ result }` envelope, but `getFilesList`
 * also returns the reviews the versions sit in; the endpoint has no response schema, so the extra
 * member is declared here — optional, because nothing guarantees it.
 */
type FilesListResponse = { result: RawFileRecord[]; reviewsData?: RawReviewRecord[] };

/** A review record as the same untyped query returns it, before `mapReviewsByVersionId` flattens it. */
type RawReviewRecord = {
  _id?: ObjectIdString;
  createdDate?: string | number;
  name?: string;
  reviewFlow?: { name?: string };
  serialId?: string | number;
  currentReviewLevel?: ReviewLevelOrSentinel;
  nextActionDueDate?: string | number;
  reviewStatus?: string;
  initiationDate?: number;
  finishedDate?: number;
  fileVersionStatuses?: { fileVersionId?: string; status?: { name?: string; category?: string } }[];
};

/**
 * Aliased to the reducer's type rather than re-declared: `formTemplateRef` is handed straight to
 * `FileUpload`, whose prop is `FileUploadFormTemplate`, so a local copy could only drift from it.
 */
type FormTemplate = FileUploadFormTemplate;

/**
 * One review as `mapReviewsByVersionId` flattens it onto a row. Not a server response type: it is a
 * projection this component builds from the reviews list, keyed by file-version id.
 */
type FileReviewSummary = {
  _id?: ObjectIdString;
  createdDate?: string | number;
  name?: string;
  reviewFlow?: { name?: string };
  status?: { name?: string; category?: string };
  serialId?: string | number;
  currentReviewLevel?: ReviewLevelOrSentinel;
  nextActionDueDate?: string | number;
  reviewStatus?: string;
  initiationDate?: number;
  finishedDate?: number;
};

/**
 * The shape SlickGrid1 declares for `onSelectedRowIdsChanged` — slickgrid's opaque event bag. Not
 * exported from the grid, so it is restated here structurally (`SlickGrid1/index.tsx`, l.106).
 */
type SlickEventArgs = Record<string, unknown> & { originalEvent?: Event };

/**
 * The selected row ids out of an `onSelectedRowIdsChanged` payload.
 *
 * SlickGrid1 declares that prop as slickgrid's opaque `SlickEventArgs` (`Record<string, unknown>`)
 * while what it dispatches is a CustomEvent carrying the DataView's own payload, on which `ids` is
 * the filtered id list — a mismatch the grid documents at its dispatch site. Neither level is
 * described by the declared type, so both are checked here rather than asserted through.
 */
const readSelectedRowIds = (args: SlickEventArgs): Array<string | number> => {
  const detail: unknown = args.detail;
  if (typeof detail !== "object" || detail === null || !("args" in detail)) return [];
  const eventArgs: unknown = detail.args;
  if (typeof eventArgs !== "object" || eventArgs === null || !("ids" in eventArgs)) return [];
  const ids: unknown = eventArgs.ids;
  const idList: unknown[] = Array.isArray(ids) ? ids : [];
  return idList.filter((id): id is string | number => typeof id === "string" || typeof id === "number");
};

/**
 * A toolbar entry as this grid builds it. The exported `ToolbarItem` calls the label `text` and
 * declares neither `name` nor `buttonType`, but `ToolBar.tsx` reads both off its own `ToolBarEntry`
 * extension and documents that call sites still passing them should extend `ToolbarItem` locally
 * rather than widen the public type. The previous `.filter(Boolean) as ToolbarItem[]` was hiding
 * that both keys were unknown on the declared element type.
 */
type FilesGridToolbarItem = ToolbarItem & { name?: string; buttonType?: string };

/** The row shape `handleFileSelectionCallback` receives — a flattened picker payload, not a file. */
type SelectedFilePayload = {
  id: unknown;
  hierarchyId: unknown;
  name: unknown;
  userId: unknown;
  dateCreated: unknown;
  versionNumber: unknown;
  fileVersion: unknown;
  size: unknown;
  conversionStatus: unknown;
  isContainer: unknown;
  parentId: unknown;
  resourceType: unknown;
  project: unknown;
  space: unknown;
  spaceId: unknown;
  fileType: unknown;
  engineType: unknown;
  isFile: unknown;
};

/**
 * The custom-field columns this grid derives from form templates. Aliased to the helper's column
 * type rather than re-declared: the local copy duplicated `ColumnDefinition` from SlickGrid1 and
 * widened it with `[key: string]: any` plus an untyped `_valueGetter`, so `extraColumns` did not
 * actually agree with what `getFilesGridColumns` merges them into.
 */
type ColumnDefinition = FilesGridColumn<FileData>;

interface FilesGridHeaderProps {
  projectId?: string | null;
  showAllVersions: boolean;
  onToggleVersions: () => void;
}

const FilesGridHeader = memo(function FilesGridHeader({
  projectId,
  showAllVersions,
  onToggleVersions,
}: FilesGridHeaderProps) {
  return (
    <div className="flex items-center gap-4 justify-between pb-4">
      <GridPageHeaderLeft
        icon="FilesIcon"
        title="Files"
        description={
          projectId
            ? "Manage all the files available in the current project"
            : "Manage all the files available in the current portal"
        }
      />
      <div className="flex items-center gap-2">
        <ToggleSwitchComponent checked={showAllVersions} onChange={onToggleVersions} />
        <span>{`Showing ${showAllVersions ? "all" : "only latest"} versions`}</span>
      </div>
    </div>
  );
});

function FilesGrid({
  initialShowAllVersions = true,
  showProjectID = false,
  projectId,
  spaceId,
  showTitle = true,
  showToolbar = { views: true, group: true, upload: true, action: true, pinnedColumns: true },
  propertiesPanel = true,
  excelExport = true,
  handleFileSelectionCallback,
  showOnlyMergedFile,
  disableMenuOptions,
  disableFiles,
}: Readonly<FilesGridProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const byPortal = searchParams.get("byPortal");
  const location = useLocation();

  const fileUploadList = useSelector((state: RootState) => state.fileUploadList);
  const fileUploadState = useSelector((state: RootState) => state.fileUploadState);
  const importOnlyUploads = useSelector((state: RootState) => !!state.layout.FILE_MANAGER?.IMPORT_ONLY_UPLOADS);
  // Portal-level upload restriction. This is the version-upload grid — a second file-picker entry
  // point that the original feature never filtered.
  const allowedFileTypes = useSelector((state: RootState) => state.layout.ALLOWED_FILE_TYPES);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [showProperties, setShowProperties] = useState<boolean>(false);
  const [gridReady, setGridReady] = useState<boolean>(false);
  const [originalFilesData, setOriginalFilesData] = useState<FileData[]>([]);
  const [filesData, setFilesData] = useState<FileData[]>([]);
  const [groupActive, setGroupActive] = useState<boolean>(false);
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<Array<string | number>>([]);
  const [customFields, setCustomFields] = useState<Record<string, ColumnDefinition>>({});
  const [showLoader, setShowLoader] = useState<boolean>(true);

  const [layoutDropdown, setLayoutDropdown] = useState<boolean>(false);
  const [showAllVersions, setShowAllVersions] = useState<boolean>(initialShowAllVersions);
  const showAllVersionsRef = useRef<boolean>(initialShowAllVersions);
  const [triggerUpdateCurrentView, setTriggerUpdateCurrentView] = useState<FilesGridSavedView | false>(false);
  const [preventUpdate, setPreventUpdate] = useState<boolean>(false);
  const preventUpdateCurrentView = useRef<boolean>(false);
  const [fileVersionId, setFileVersionId] = useState<string[]>([]);
  const formTemplateRef = useRef<FormTemplate>({ template: [], formTemplateId: "" });
  const fileUploadStateRef = useRef<string | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    // `event.target` is an `EventTarget`; `contains` needs a `Node`. Checked rather than asserted —
    // a document click always targets a node, so the guard never changes what closes the dropdown.
    if (popupRef.current && event.target instanceof Node && !popupRef.current.contains(event.target)) {
      layoutDropdown && setLayoutDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    fetchAllUsedFormTemplates();
    if (!byPortal && projectId) {
      getFormTemplate();
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fileUploadStateRef.current = fileUploadState;
    if (fileUploadState === "CLOSED") {
      if (reactGridRef.current?.dataView) {
        loadData(0, 1000000000);
      }
      dispatch(setIsFileVersionUploading(false));
      dispatch(closeFileUpload());
      dispatch(emptyFileUploadData());
    }
  }, [fileUploadState]);

  useEffect(() => {
    if (fileUploadState === "CLOSED") return;
    if (fileUploadList.length) {
      dispatch(minimiseFileUpload());
    } else {
      dispatch(closeFileUpload());
    }
  }, [location]);

  const handleUpdateCurrentView = (data: FilesGridSavedView) => {
    if (!preventUpdateCurrentView.current) {
      setTriggerUpdateCurrentView(data);
    }
  };

  useEffect(() => {
    preventUpdateCurrentView.current = preventUpdate;
  }, [preventUpdate]);

  useEffect(() => {
    showAllVersionsRef.current = showAllVersions;
  }, [showAllVersions]);

  // Helper function to filter files to only latest versions
  const filterToLatestVersions = (data: FileData[]): FileData[] => {
    const latestDataMap = new Map<string, FileData>();
    for (const item of data) {
      const projectId = item.file?.space?.project?.projectId;
      const indexAtProjectLevel = item.file?.indexAtProjectLevel;
      if (projectId === undefined || indexAtProjectLevel === undefined) {
        continue;
      }
      const key = `${projectId}-${indexAtProjectLevel}`;
      const existingItem = latestDataMap.get(key);
      // `fileVersionNumber` is not guaranteed by the query. A row missing it never displaces the
      // one already held — the same outcome the bare `>` gave, since any comparison against
      // `undefined` is false.
      if (
        !existingItem ||
        (item.fileVersionNumber !== undefined &&
          existingItem.fileVersionNumber !== undefined &&
          item.fileVersionNumber > existingItem.fileVersionNumber)
      ) {
        latestDataMap.set(key, item);
      }
    }
    return Array.from(latestDataMap.values());
  };

  async function onClickLayout(clickLayout: FilesGridSavedView): Promise<void> {
    /*
      Both syncronours and asyncronous update is required
    */
    setPreventUpdate(true);
    preventUpdateCurrentView.current = true;
    /*
      end
    */

    const params = clickLayout;

    if (Object.keys(params).length !== 0) {
      // Except "All Files" View
      const { group, pinnedColumns, enableFiltering, enableGrouping, enableAllVersions } = params;

      // The initial saved view resolves before the grid mounts; apply the state parts now
      // (they belong in the same batch as the initial fetch) and replay the grid-instance
      // operations once the grid is ready.
      if (!reactGridRef.current?.slickGrid) {
        pendingLayoutParamsRef.current = params;
        setGroupActive(!!enableGrouping);
        setFilterActive(!!enableFiltering);
        setShowAllVersions(!!enableAllVersions);
        setPreventUpdate(false);
        return;
      }

      if (Array.isArray(group) && group.length) {
        // The `!reactGridRef.current?.slickGrid` guard above already returned, so the grid is there.
        // BUG FIXED HERE: this used to pass the whole `GroupCondition` object as `column`, which the
        // grid then looked up as a column id (`resolveCol(gc.column)`), so the grouping it applied
        // could never resolve a column. Each condition is now forwarded with its own `column`, and
        // only the `direction` default is added.
        reactGridRef.current!.gridState!.setGroupConditions!(
          group.map((groupItem: GroupCondition) => ({
            ...groupItem,
            direction: groupItem.direction || "asc",
          })),
        );
        // `gridService._grid` is the same SlickGrid instance the ref exposes as `slickGrid`
        // (`GridService.init` assigns it), so the public handle is used instead of the protected one.
        reactGridRef.current!.slickGrid.invalidate();
      } else if (pinnedColumns) {
        // KNOWN BUG, verified: `pinnedColumns` is the string[] of pinned column IDS that the grid
        // persists, while `frozenColumn` is a column INDEX — so passing it through only ever landed
        // on a non-number that SlickGrid's `setFrozenOptions` normalises straight back to -1, i.e.
        // "no frozen column". That outcome is what is expressed here; the view carrying no
        // `pinnedColumns` at all left the option untouched, so that case still skips the call.
        // Restoring real pinning from a saved view is a SlickGrid1 change (it only applies its own
        // `pinnedColumns` when `enableGroupingOption` is off) — reported, not guessed at here.
        reactGridRef.current!.slickGrid.setOptions({ frozenColumn: -1 });
      }
      setGroupActive(!!enableGrouping);
      setFilterActive(!!enableFiltering);
      setShowAllVersions(!!enableAllVersions);
    }
    setPreventUpdate(false);
  }

  const getFormTemplate = async (): Promise<void> => {
    const response = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/projects/getFormTemplate`, {
      projectId,
      resourceType: formCategories.files,
    });

    if (response.data.status && response.data.data) {
      const formData = response.data.data;
      const template = (formData.template || []) as FormTemplate["template"];
      formTemplateRef.current = {
        template,
        formTemplateId: formData._id,
      };
    }
  };

  const [filesGridColumns, setFilesGridColumns] = useState<FilesGridColumn<FileData>[]>([]);

  const createColumnFromQuestion = (
    question: { _id: string; fieldType?: string; questionValue?: string },
    sectionHeading: string | undefined,
  ): ColumnDefinition => {
    const typeMap: Record<string, NonNullable<ColumnDefinition["filterType"]>> = {
      "single-line": "Text",
      "multi-line": "Text",
      "date-time": "Date",
      "multi-select": "MultiSelect",
    };

    // An absent or unmapped `fieldType` leaves the column with no filter type. That used to be
    // spelled `""`, which is not a `FilterType`; every read of it in the grid is either
    // `filterType || "Text"` or an equality test, so `undefined` behaves identically.
    const filterType = question.fieldType === undefined ? undefined : typeMap[question.fieldType];

    const columnDef: ColumnDefinition = {
      field: `customFields.${question._id}`,
      id: `${question._id}`,
      width: 150,
      originalWidth: 150,
      textAlign: "Left",
      headerText: question.questionValue,
      filterType,
      _valueGetter: (args: FileData) => {
        const value = _.get(args, `customFields.${question._id}`);
        if (question.fieldType === "date-time") {
          if (!value) return null;
          return Number(value);
        }
        return value;
      },
      _excelValueGetter: (args: { customFields?: Record<string, unknown> }) => {
        const value = args.customFields?.[question._id];
        if (value === null || value === undefined || value === "") return "";
        if (question.fieldType === "date-time") return convertDate(Number(value), "dateForTooltip");
        return value;
      },
      name: question.questionValue,
      columnGroup: sectionHeading,
    };

    // Add formatter for date-time fields to display formatted date
    if (question.fieldType === "date-time") {
      columnDef.formatter = (_row: number, _cell: number, _value: unknown, _columnDef, dataContext: FileData) => {
        const value = _.get(dataContext, `customFields.${question._id}`);
        if (!value) return "";
        return convertDate(Number(value), "dateForText");
      };
    }

    return columnDef;
  };

  const processQuestionFields = (
    questionFields: FormTemplateQuestion[] | undefined,
    sectionHeading: string | undefined,
    extraColumns: Record<string, ColumnDefinition>,
  ): void => {
    if (!questionFields) return;
    for (const question of questionFields) {
      extraColumns[question._id] = createColumnFromQuestion(question, sectionHeading);
    }
  };

  const processTemplate = (
    template: FormTemplateSection[] | undefined,
    extraColumns: Record<string, ColumnDefinition>,
  ): void => {
    if (!template) return;
    for (const section of template) {
      const { questionFields, sectionHeading } = section;
      processQuestionFields(questionFields, sectionHeading, extraColumns);
    }
  };

  const processTemplatesData = (data: { template?: FormTemplateSection[] }[]): Record<string, ColumnDefinition> => {
    const extraColumns: Record<string, ColumnDefinition> = {};
    for (const item of data) {
      const { template } = item;
      processTemplate(template, extraColumns);
    }
    return extraColumns;
  };

  const fetchAllUsedFormTemplates = async (): Promise<void> => {
    let extraColumns: Record<string, ColumnDefinition> = {};

    if (projectId) {
      const response = await axiosWrapper.post<GetAllUsedFormTemplatesResponse>(
        `${getBackendUrl()}/projects/getAllUsedFormTemplates`,
        { projectId },
      );
      extraColumns = processTemplatesData(response.data.data ?? []);
      setCustomFields(extraColumns);
    }

    // Explicit type argument: inference would otherwise fall back to the `FilesGridRow` default and
    // reject these templates, which take the narrower `FileData` (parameter contravariance). Erased.
    const allColumns = getFilesGridColumns<FileData>({
      userAvatar,
      formatDate,
      statusInReviewTemplate,
      currentReviewLevelFormat,
      // actionFormatter,
      showProjectID,
      extraColumns,
      getReviewStatus,
      reactGridRef,
    });

    setFilesGridColumns(allColumns);
  };

  const formatDate = (params: FileData): HTMLDivElement => {
    // Plain text + native `title` tooltip — a React root + MUI Tooltip per date
    // cell (previous code) was a top scroll cost and leaked the root on recycle.
    const container = document.createElement("div");
    container.style.overflow = "hidden";
    container.style.textOverflow = "ellipsis";
    container.style.whiteSpace = "nowrap";
    container.title = String(convertDate(Number(params.createdDate), "dateForTooltip"));
    container.textContent = String(convertDate(Number(params.createdDate), "dateForText"));
    return container;
  };

  const userAvatar = (args: FileData): HTMLDivElement => {
    const container = document.createElement("div");
    renderUserTag(container, { id: args.createdBy?._id });
    return container;
  };

  const getReviewStatus = (args: FileData): string => {
    const status = _.get(args, "reviews.reviewStatus");
    const currentReviewLevel = args.currentReviewLevel;
    if (currentReviewLevel === "Varies" || currentReviewLevel === "") {
      return currentReviewLevel;
    }
    if (currentReviewLevel) {
      // `ReviewLevelOrSentinel` is a level object or one of the two grouped-row sentinels handled
      // above; anything else is already display text. Reading `.title` off the string arm (which is
      // what the assertion here used to do) produced `undefined` and rendered an empty cell.
      return typeof currentReviewLevel === "string" ? currentReviewLevel : (currentReviewLevel.title ?? "");
    }
    if (status === "Closed") {
      return "Review Closed";
    } else if (status === "Revoked") {
      return "Review Revoked";
    } else if (status === "Draft") {
      return "Review Draft";
    } else if (status === "Open") {
      return "Review Open";
    }
    return "";
  };

  const currentReviewLevelFormat = (value: FileData): HTMLDivElement => {
    const container = document.createElement("div");
    const span = document.createElement("span");
    span.className = "italic text-gray-500";
    span.textContent = getReviewStatus(value);
    container.appendChild(span);
    return container;
  };

  const statusInReviewTemplate = (args: FileData): string | HTMLDivElement => {
    if (!_.get(args, "reviews.status.name")) {
      return "-";
    }

    const container = document.createElement("div");

    const { name, category } = args.reviews!.status!;
    const style = getStatusBtnStyle(category);

    const chip = document.createElement("p");
    chip.className = "w-fit px-2 py-1 rounded-lg text-white font-semibold text-xs";
    Object.assign(chip.style, style);
    chip.textContent = String(name);
    container.appendChild(chip);
    return container;
  };

  const handleFileOpenClick = (params: FileData): void => {
    const { fileVersionId, file } = params;
    const url = getViewerUrl({
      fileVersionId: fileVersionId || "",
      spaceId: _.get(file, "space.id"),
      projectId: (_.get(file, "space.project.id") || projectId) ?? "",
    });

    window.open(url, "_blank");
  };

  const handleReviewOpenClick = (_e: unknown, args: FileData): void => {
    // Erased assertions: this runs only for a row the user selected in a review context.
    const spaceId = args.file!.space!.id;
    const projectId = args.file!.space!.project!.id;
    const reviewId = args.reviews!._id;
    if (reviewId) {
      const url = getReviewUrl(projectId, spaceId, reviewId);
      window.open(url, "_blank");
    }
  };

  const handleOpenResource = (_e: unknown, params: FileData): void => {
    if (params.isContainer) {
      dispatch(
        showToastNotification({
          content: t("Cannot open container"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      handleFileOpenClick(params);
    }
  };

  const handleViewProperties = async (): Promise<void> => {
    const args = reactGridRef.current!.dataView.getAllSelectedItems();
    const fileVersionIds = args.map((data: FileData) => data.fileVersionId!);
    setFileVersionId(fileVersionIds);
  };

  useEffect(() => {
    // `selectedRowId` is only a dependency here — it is an array, so it is always truthy and the
    // `&& selectedRowId` that used to be in this condition never gated anything. The effect runs on
    // every selection change while the properties panel is open, exactly as before.
    if (showProperties) {
      handleViewProperties();
    }
  }, [showProperties, selectedRowId]);

  const handlePropertiesOpen = (): void => {
    const next = !showProperties;
    setShowProperties(next);
    if (!next) {
      setFileVersionId([]);
    }
  };

  // `select` hands back `string | number`; this handler only ever receives the numeric ids 1 and 2
  // declared on `items` below. Widened rather than asserted at the prop — no runtime change.
  const handleActionClick = (data: string | number): void => {
    const args = reactGridRef.current!.dataView.getAllSelectedItems()[0];
    if (data === 1) {
      handleOpenResource(undefined, args!);
    } else if (data === 2) {
      handleReviewOpenClick(undefined, args!);
    }
  };

  const handleRowSelection = (args: SlickEventArgs): void => {
    // `getDataItem` is declared to return the row type but hands back `undefined` for a row index
    // the view no longer holds (a selection kept across a refresh or filter), which is exactly what
    // the `file` check below is for. Widened on assignment so that hole stays visible.
    const selectedData: Array<FileData | undefined> = reactGridRef
      .current!.slickGrid.getSelectedRows()
      .map((data: number) => reactGridRef.current!.slickGrid.getDataItem(data));
    const filteredData = selectedData.filter((file): file is FileData => !!file && !file.isRowDisabled);
    handleFileSelectionCallback?.(
      filteredData.map((file: FileData) => {
        return {
          id: file._id,
          hierarchyId: file._id,
          name: file.fileName,
          userId: file.createdBy?._id,
          dateCreated: file.createdDate,
          versionNumber: file.fileVersionNumber,
          fileVersion: file.fileVersionId,
          size: file.size,
          conversionStatus: file.conversionStatus,
          isContainer: file.isContainer,
          parentId: file.parentId,
          resourceType: file.resourceType,
          project: file.file!.space!.project,
          space: file.space,
          spaceId: file.spaceId,
          fileType: file.fileType,
          engineType: file.engineType,
          isFile: file.isFile,
        };
      }),
    );

    setSelectedRowId(readSelectedRowIds(args));
  };

  const loadData = async (skip: number, take: number): Promise<void> => {
    // setShowLoader(true);

    const response = await fetchFilesData(skip, take);
    const reviewsByVersionId = mapReviewsByVersionId(response.reviewsData ?? []);
    const fullResponse = buildFilesWithReviews(response.result, reviewsByVersionId);
    const modifiedResponse =
      Array.isArray(disableFiles) && disableFiles.length
        ? fullResponse.map((file: FileData) =>
            file.fileVersionId && disableFiles.includes(file.fileVersionId) ? { ...file, isRowDisabled: true } : file,
          )
        : fullResponse;

    setOriginalFilesData(modifiedResponse);

    // Apply version filtering based on showAllVersions ref (use ref to avoid stale closure)
    if (showAllVersionsRef.current) {
      setFilesData(modifiedResponse);
    } else {
      setFilesData(filterToLatestVersions(modifiedResponse));
    }

    setShowLoader(false);

    // Force grid to re-render cells after data loads to ensure formatters/valueGetters are applied
    requestAnimationFrame(() => {
      if (reactGridRef.current?.slickGrid) {
        reactGridRef.current!.slickGrid.invalidate();
      }
    });
  };

  // Fetch files from service
  const fetchFilesData = async (skip: number, take: number): Promise<FilesListResponse> => {
    const queryData = new QueryBuilder()
      .where("parentFileVersionId", "equal", null)
      .requiresCount()
      .skip(skip)
      .take(take);

    if (projectId) {
      queryData.addParams("projectId", projectId);
    }

    if (spaceId) {
      queryData.addParams("spaceId", spaceId);
    }

    if (showOnlyMergedFile) {
      queryData.addParams("showOnlyMergedFile", showOnlyMergedFile);
    }

    return await executeQuery<RawFileRecord>(getFilesList, queryData);
  };

  const mapReviewsByVersionId = (reviewsData: RawReviewRecord[]): Map<string, FileReviewSummary[]> => {
    const map = new Map<string, FileReviewSummary[]>();
    for (const review of reviewsData) {
      for (const { fileVersionId, status } of review.fileVersionStatuses!) {
        if (!fileVersionId) continue;
        const reviews = map.get(fileVersionId!) || [];
        reviews.push({
          _id: review._id,
          createdDate: review.createdDate,
          name: review.name,
          reviewFlow: review.reviewFlow,
          status,
          serialId: review.serialId,
          currentReviewLevel: review.currentReviewLevel,
          nextActionDueDate: review.nextActionDueDate,
          reviewStatus: review.reviewStatus,
          initiationDate: review.initiationDate,
          finishedDate: review.finishedDate,
        });
        map.set(fileVersionId!, reviews);
      }
    }
    return map;
  };

  // Build files array with reviews
  const buildFilesWithReviews = (filesData: RawFileRecord[], reviewsByVersionId: Map<string, FileReviewSummary[]>) => {
    const result: FileData[] = [];

    for (const data of filesData) {
      setFileType(data);

      const reviews = reviewsByVersionId.get(data.id?.toString()!) || [];

      // The only member `FileData` requires is `id`, SlickGrid's row key, and the files query has
      // no response schema to promise even that. It is read here at the primitive the grid indexes
      // rows by — `String()` is identity for the string ids this endpoint returns, and is already
      // what the review branch below does through its template literal.
      const rowId: string | number = typeof data.id === "number" ? data.id : String(data.id);

      if (reviews.length > 0) {
        for (const [index, review] of reviews.entries()) {
          result.push({
            ...data,
            reviews: review,
            id: `${rowId}_${index}`,
            reviewName: review.name,
            serialId: review.serialId,
            reviewFlow: review.reviewFlow!.name,
            currentReviewLevel: review.currentReviewLevel,
            reviewStatusName: review.status!.name,
            nextActionDueDate: review.nextActionDueDate,
          });
        }
      } else {
        result.push({ ...data, id: rowId, reviews: {} });
      }
    }

    return result;
  };

  // Helper: set fileType
  const setFileType = (data: RawFileRecord) => {
    if (data.resourceType === "RESOURCE_TYPE_FILE") {
      const fileName = data.fileName;
      data.fileType = fileName!.includes(".") ? fileName!.substring(fileName!.lastIndexOf(".") + 1).toUpperCase() : "";
    } else {
      data.fileType = "";
    }
  };

  useEffect(() => {
    loadData(0, 1000000000);
  }, []);

  const reactGridRef = useRef<SlickGrid1Ref<FileData> | null>(null);
  const pendingLayoutParamsRef = useRef<FilesGridSavedView | null>(null);

  useEffect(() => {
    if (gridReady && pendingLayoutParamsRef.current) {
      const pendingParams = pendingLayoutParamsRef.current;
      pendingLayoutParamsRef.current = null;
      // Replay after the grid's own view application (it runs in the grid-mounted commit),
      // matching the old ordering where this handler ran after the grid applied the view.
      // suppressViewUpdates keeps the replayed state changes from re-persisting the view.
      setTimeout(() => {
        const grid = reactGridRef.current;
        if (!grid?.slickGrid) return;
        if (grid.suppressViewUpdates) {
          grid.suppressViewUpdates(() => onClickLayout(pendingParams));
        } else {
          onClickLayout(pendingParams);
        }
      }, 0);
    }
  }, [gridReady]);

  useEffect(() => {
    if (gridReady) {
      handleUpdateCurrentView({
        enableGrouping: groupActive,
      });
    }
  }, [gridReady, groupActive]);

  useEffect(() => {
    handleUpdateCurrentView({
      enableAllVersions: showAllVersions,
    });
  }, [showAllVersions]);

  const handleFileUpload = (): void => {
    if (importOnlyUploads) {
      return;
    }
    if (fileUploadState === "MINIMISED") {
      dispatch(
        showToastNotification({
          content: t("Another upload is in progress. Please close that popup to proceed."),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }

    dispatch(
      setCurrentFileUploadProperties({
        fileAllowedExtensions: {
          minFileSize: 0,
          maxFileSize: MAX_FILE_UPLOAD_SIZE,
          allowedExtensions: buildAllowedExtensionsAccept(allowedFileTypes),
        },
        isUploadAllowed: true,
        // getPermission,
        formTemplate: formTemplateRef.current,
      }),
    );
    dispatch(setCurrentFolderInFileManager({ hierarchyId: "0" }));
    dispatch(maximiseFileUpload());
  };

  const handleGroupActive = (): void => {
    setGroupActive(!groupActive);
  };

  const handleFilterActive = (): void => {
    setFilterActive(!filterActive);
  };

  const handleShowAllVersions = useCallback((): void => {
    setShowAllVersions((prev) => {
      if (prev) {
        setFilesData(filterToLatestVersions(originalFilesData));
      } else {
        setFilesData(originalFilesData);
      }
      return !prev;
    });
  }, [originalFilesData]);

  const updateItemData = (
    item: FileData,
    fileVersionIdToMatch: string[] | null,
    dataKey: string,
    dataValue: unknown,
  ): boolean => {
    if (!fileVersionIdToMatch || !Array.isArray(fileVersionIdToMatch)) return false;
    if (!fileVersionIdToMatch.includes(String(item.id))) return false;
    _.update(item, dataKey, () => dataValue);
    return true;
  };

  const processDataKey = (
    dataKey: string,
    data: Record<string, unknown>,
    items: FileData[],
    fileVersionId: string[] | null,
  ): boolean => {
    return items.some((item: FileData) => updateItemData(item, fileVersionId, dataKey, data[dataKey]));
  };

  const onChange = (data: Record<string, unknown>): void => {
    const { dataView, slickGrid } = reactGridRef.current!;
    // `getItems()` returns the DataView's own `items` array (the same reference the protected field
    // holds), so the rows updated below are the rows the grid renders.
    const items = dataView.getItems();

    for (const dataKey of Object.keys(data)) {
      processDataKey(dataKey, data, items, fileVersionId);
    }

    slickGrid.invalidate();
  };

  const actionTemplate = (): React.ReactElement => {
    return (
      <TypedDropdownButtonComponent
        disabled={selectedRowId.length !== 1}
        // buttonType={BUTTON_TYPES.DROPDOWN_BACKGROUND_BUTTON}
        content={t("Action")}
        select={handleActionClick}
        items={[
          {
            text: t("Open File"),
            id: 1,
          },
          {
            text: t("Open Review"),
            id: 2,
          },
        ]}
      />
    );
  };

  const toolbarClickHandler = (args: string): void => {
    if (args === "upload") {
      handleFileUpload();
    } else if (args === "group") {
      handleGroupActive();
    } else if (args === "filter") {
      handleFilterActive();
    }
    if (args === "properties") {
      handlePropertiesOpen();
    }
  };

  const toolbar: FilesGridToolbarItem[] = [
    showToolbar.upload && !importOnlyUploads
      ? {
          name: t("Upload"),
          id: "upload",
          buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
        }
      : undefined,
    showToolbar.action ? { id: "action", template: actionTemplate } : undefined,
  ].filter((item) => item !== undefined);

  const [enableWrapText, setEnableWrapText] = useState<boolean>(false);

  return (
    <div className="flex gap-4 h-full w-full">
      <SplitLayout
        initialSizes={showProperties ? [66, 34] : [100, 0]}
        gutterWidth={"0.25rem"}
        minSizes={[50, 20]}
        style={{ display: "flex", height: "100%" }}
        showProperties={showProperties}
      >
        <GridPageLayout
          showHeader={showTitle}
          customHeader={
            <FilesGridHeader
              projectId={projectId}
              showAllVersions={showAllVersions}
              onToggleVersions={handleShowAllVersions}
            />
          }
          className="min-w-0"
          gridContainerClassName="items-center justify-center overflow-auto"
        >
          {filesGridColumns.length > 0 && (
            <>
              {showLoader ? (
                <CircularProgress />
              ) : (
                <SlickGrid1
                  fitColumnWidthToHeader
                  columnDefinitions={
                    // `ValidatedColumnDefinition.__brand` is a phantom member — a `declare const
                    // unique symbol` with no runtime representation, so no value can ever carry it
                    // and there is nothing to validate (SlickGrid1's own `brandColumns()` carries
                    // the same disable for the same reason). `typedColumns()` cannot mint these:
                    // the custom-field columns `getFilesGridColumns` merges in address values by
                    // dotted path (`customFields.<questionId>`), which never satisfies its
                    // `keyof TRow` pairing.
                    //
                    // `withDynamicColumns()` is the sanctioned brander for exactly that mix and is
                    // what this boundary SHOULD call, but its parameter is
                    // `Array<ValidatedColumnDefinition<TRow> | Record<string, unknown>>`, and
                    // `FilesGridColumn<TRow>` (FilesGridHelper.ts:83) intersects the `ColumnDefinition`
                    // INTERFACE, so it gets no implicit index signature and is not assignable to
                    // `Record<string, unknown>` (TS2345). The fix is one line outside this file —
                    // either `getFilesGridColumns` returning `withDynamicColumns(...)`, or
                    // `withDynamicColumns` taking `brandColumns`' own (already wider) parameter type,
                    // which does admit `ColumnDefinition<TRow, keyof TRow>`.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- phantom brand with no runtime representation; blocked on the two-way gap above between FilesGridColumn and withDynamicColumns' parameter type
                    filesGridColumns as typeof filesGridColumns & ValidatedColumnDefinition<FileData>[]
                  }
                  enableWrapText={enableWrapText}
                  setEnableWrapText={setEnableWrapText}
                  gridOptions={{
                    showPreHeaderPanel: Object.keys(customFields).length > 0,
                    enableAutoSizeColumns: false,
                  }}
                  onClickLayout={onClickLayout}
                  onSelectedRowIdsChanged={handleRowSelection}
                  setGridReady={setGridReady}
                  triggerUpdateCurrentView={triggerUpdateCurrentView}
                  dataset={filesData}
                  enableGroupingOption
                  enableFilteringOption
                  enableSearchBar
                  {...(projectId &&
                    showToolbar.views && {
                      viewsOptions: {
                        headerName: "Views",
                        name: "View",
                        resourceType: "FILES",
                        selectedLayoutKey: "FILES_SELECTED_LAYOUT",
                        fetchParams: {
                          projectId,
                        },
                      },
                    })}
                  enableViews={!!(projectId && showToolbar.views)}
                  disableMenuOptions={disableMenuOptions}
                  enableSortingOption
                  enableColumnChooser
                  enableRowResize={true}
                  toolbar={toolbar}
                  handleUpdateCurrentView={handleUpdateCurrentView}
                  ref={reactGridRef}
                  excelExport={excelExport}
                  toolbarClickHandler={toolbarClickHandler}
                  propertiesPanel={propertiesPanel}
                />
              )}
            </>
          )}
        </GridPageLayout>
        {showProperties && (
          <div className="w-full h-full">
            <FileVersion
              fileVersionId={fileVersionId}
              isContainer={reactGridRef.current!.dataView.getAllSelectedItems()[0]?.isContainer}
              onChange={onChange}
              disableEngineType={false}
            />
          </div>
        )}
      </SplitLayout>
    </div>
  );
}

export default FilesGrid;
